// "What does the VENDOR still need to act on" — the rule behind the portal's
// Questionnaire nav badge, shared by the browser and the API Worker.
//
// Moved here from packages/web-shared (which re-exports every name below) so
// the API can compute the badge itself: GET /api/me/nav-counts and GET
// /api/driver/me now carry the count, and the portal shell no longer keeps the
// full question and answer lists live on every page just to draw one number.
// One implementation is the only way the server's number and the wizard's own
// per-question flags cannot drift; the parity tests pin the two adapters (the
// web-shared Question/Submission one and the API's row one) to it.
//
// Structural types only, like progress.ts: each caller passes its own shapes.
import {
  computeProgress,
  isQuestionComplete,
  type CompletionReason,
  type ProgressQuestion,
  type ProgressSubmission,
} from './progress.ts';
import { hasPendingResubmit } from './resubmitTurn.ts';

export type ActionReason = 'rejected' | 'admin_comment' | 'unanswered' | 'document_required';
export type VendorActionItem = { questionId: string; reason: ActionReason };

type ActionQuestion = { id: string; required: boolean };

/** The review fields of one answer, as the VENDOR submissions endpoints return them. */
export type ReviewSubmissionFields = {
  status: string;
  latest_comment_author?: 'admin' | 'vendor' | null;
  // The explicit-resubmit stamp and the admin's last review. Optional so older
  // callers (and fixtures) that carry only status + author keep compiling;
  // absent reads as "no resubmit", which is the previous behaviour.
  resubmitted_at?: string | null;
  reviewed_at?: string | null;
  // When the newest PUBLISHED admin comment was WRITTEN (created_at; the vendor
  // submissions endpoints derive it from the vendor-visible thread). A thread
  // reply after a resubmit does not stamp reviewed_at, so this is what hands
  // the turn back to the vendor. Absent reads as "no admin comment".
  latest_admin_comment_at?: string | null;
};

/** The visible set and per-question completion, as computeProgress returns them. */
export type ActionProgress = {
  visibleIds: ReadonlySet<string>;
  completionByQuestion: ReadonlyMap<string, CompletionReason | null>;
};

// Completion reasons that mean "a document is missing" rather than "no answer".
const DOCUMENT_REASONS: ReadonlySet<CompletionReason> = new Set<CompletionReason>([
  'upload_required',
  'adoption_required',
  'signature_required',
]);

/**
 * Whether a single submission is currently the VENDOR's turn — i.e. the vendor
 * still owes a response. True when the admin sent it back (rejected), or left the
 * most recent comment and the vendor has not resubmitted since. Approved /
 * not_applicable items are "cleared" and never count (a stale admin comment on an
 * approved item does not reopen it). A plain pending item — vendor submitted, no
 * admin response yet — is awaiting ADMIN, so this returns false: there is nothing
 * for the vendor to action.
 */
export function isAwaitingVendor(sub: ReviewSubmissionFields): boolean {
  if (sub.status === 'approved' || sub.status === 'not_applicable') return false;
  // A sent-back answer stays the vendor's until they press "Submit to admin":
  // the resubmit route (POST .../submissions/:questionId/resubmit) is the only
  // way out of 'rejected' — it flips the row to 'pending' — so here, unlike the
  // sister repo where a rework keeps status 'rejected', the status alone is a
  // reliable signal. It deliberately ignores the resubmit stamp: a vendor edit
  // nulls reviewed_at, so an OLD round's resubmitted_at would otherwise read as
  // a fresh resubmit on an answer the admin has since sent back again.
  if (sub.status === 'rejected') return true;
  // A comment the admin left with the rejection is still the latest one after
  // the vendor resubmits (a resubmit posts no comment), so without this arm a
  // resubmitted answer stayed "action required" until the admin's next move.
  if (sub.latest_comment_author === 'admin') return !hasPendingResubmit(sub);
  return false;
}

/**
 * Whether the vendor owes an action on a single question.
 *
 * - Admin sent it back (rejected), or commented last and the vendor has not
 *   resubmitted since (isAwaitingVendor) → always an action. Only PUBLISHED
 *   admin activity can get here: the vendor submissions endpoints strip the
 *   draft_* verdict columns and derive latest_comment_author from published
 *   comments only, so an unpublished admin draft never reaches this function.
 * - Approved / not_applicable → never an action, whatever else is true.
 * - Required + visible + incomplete → 'unanswered', or 'document_required' when
 *   what is missing is an upload / adoption / signature.
 *
 * Unlike the sister repo there is no optional-question 'document_required'
 * arm: this repo's submit gate (computeProgress, stepBlockers) counts REQUIRED
 * questions only, so flagging an optional one would point the vendor at a row
 * nothing requires. There is likewise no exclude_from_compliance filter — the
 * column does not exist here.
 *
 * `completionByQuestion` holds visible questions only (computeProgress), so a
 * hidden question reads as complete; callers that also need the review arm to
 * skip hidden questions go through questionnaireActionItems.
 */
export function needsVendorAction(
  question: ActionQuestion,
  submission: ReviewSubmissionFields | undefined,
  completionByQuestion: ReadonlyMap<string, CompletionReason | null>,
): VendorActionItem | null {
  if (submission && isAwaitingVendor(submission)) {
    return {
      questionId: question.id,
      reason: submission.status === 'rejected' ? 'rejected' : 'admin_comment',
    };
  }
  if (submission && (submission.status === 'approved' || submission.status === 'not_applicable')) {
    return null;
  }
  if (!question.required) return null;
  const reason = completionByQuestion.get(question.id);
  // `== null` covers both complete (null) and hidden (absent from the map).
  if (reason == null) return null;
  return {
    questionId: question.id,
    reason: DOCUMENT_REASONS.has(reason) ? 'document_required' : 'unanswered',
  };
}

/** Whether an item was handed back by the admin (as opposed to never finished). */
export function isReturnedReason(reason: ActionReason): boolean {
  return reason === 'rejected' || reason === 'admin_comment';
}

/**
 * Whether the vendor can actually DO something about an item.
 *
 * Before the questionnaire is submitted every item is actionable — the form is
 * open. After the submit, isAnswerLocked (@l2u/shared/submissionLock) locks
 * every answer except a rejected one, including a question with no answer row
 * at all, and the server refuses the write. So post-submit only a RETURNED
 * item is actionable: rejected (the row reopens) or an admin comment (the
 * vendor can reply on the thread). An 'unanswered' / 'document_required' item
 * on a locked question — a follow-up revealed by editing a rejected parent, a
 * question added after the submit — is not flagged: nothing on screen could
 * clear it.
 *
 * The ONE predicate every consumer goes through, via questionnaireActionItems:
 * the wizard's cards, filter and stepper dots, the picker pill, the banner and
 * the nav badge.
 */
export function isActionable(item: VendorActionItem, submitted: boolean): boolean {
  return !submitted || isReturnedReason(item.reason);
}

/**
 * Every ACTIONABLE item among one questionnaire's questions, in input order
 * (see isActionable for what `submitted` changes). Questions outside
 * `progress.visibleIds` are skipped entirely — including a rejected one: the
 * wizard never renders a hidden question, so counting it would badge work the
 * vendor has no way to reach.
 */
export function questionnaireActionItems(
  questions: ActionQuestion[],
  subByQ: ReadonlyMap<string, ReviewSubmissionFields>,
  progress: ActionProgress,
  opts: { submitted: boolean },
): VendorActionItem[] {
  const items: VendorActionItem[] = [];
  for (const q of questions) {
    if (!progress.visibleIds.has(q.id)) continue;
    const item = needsVendorAction(q, subByQ.get(q.id), progress.completionByQuestion);
    if (item && isActionable(item, opts.submitted)) items.push(item);
  }
  return items;
}

/**
 * Whether the vendor has submitted THIS questionnaire.
 *
 * Driven entirely by per-answer persisted signals, never by the company-wide
 * status. `companies.status` is shared across every questionnaire, so a vendor
 * in 'review' for questionnaire A would otherwise have a brand-new
 * questionnaire B reported as submitted before they had touched it.
 *
 * `POST /submit` stamps `submitted_at` on every answer it submits, so a
 * questionnaire counts as submitted once any of its answers carries one. The
 * reviewed statuses are a defensive fallback for rows predating the stamp —
 * approved / not_applicable / rejected only exist post-submit.
 *
 * An unsubmitted draft (status 'pending', `submitted_at` null) never counts:
 * completing the answers is not submitting them.
 */
export function questionnaireSubmitted(
  questions: { id: string }[],
  subByQ: ReadonlyMap<string, { status: string; submitted_at?: string | null }>,
): boolean {
  return questions.some((q) => {
    const sub = subByQ.get(q.id);
    if (!sub) return false;
    if (sub.submitted_at != null) return true;
    return sub.status === 'approved' || sub.status === 'not_applicable' || sub.status === 'rejected';
  });
}

/**
 * The visible set and each visible question's completion, from the shared
 * progress rules — what the web-shared computeProgress wrapper has always
 * handed questionnaireActionItems.
 */
export function actionProgress(
  questions: ProgressQuestion[],
  submissions: ProgressSubmission[],
): ActionProgress {
  const { visibleIds } = computeProgress(questions, submissions);
  const subByQ = new Map<string, ProgressSubmission>();
  for (const s of submissions) subByQ.set(s.question_id, s);
  const completionByQuestion = new Map<string, CompletionReason | null>();
  for (const q of questions) {
    if (!visibleIds.has(q.id)) continue;
    completionByQuestion.set(q.id, isQuestionComplete(q, subByQ.get(q.id)));
  }
  return { visibleIds, completionByQuestion };
}

// ---------------------------------------------------------------------------
// The wire shapes: what GET .../questions and GET .../submissions return, read
// from the same objects on both sides — the API's loader rows before they are
// serialised, the portal's parsed JSON after. Counting from THESE through one
// adapter is what makes the server's badge and the portal's fallback the same
// number by construction, not by two mappings kept in step.

export type WireQuestion = {
  id: string;
  type: string;
  required: boolean;
  requires_upload: boolean;
  parent_id: string | null;
  condition: unknown;
  visibility_rule: unknown;
  adoptable_doc_id: string | null;
  video_url?: string | null | undefined;
  questionnaire_id: string | null;
  adoption_triggers: { trigger_value: string; document_id: string }[];
};

export type WireSubmission = ReviewSubmissionFieldsWire & {
  question_id: string;
  answer: unknown;
  submitted_at?: string | null | undefined;
  submission_files: { id: string; option_key?: string | null | undefined }[];
  submission_adoptions: {
    document_id: string;
    option_key?: string | null | undefined;
    mode?: string | undefined;
    signer_name?: string | null | undefined;
    signature_data_url?: string | null | undefined;
    requires_signature?: boolean | undefined;
  }[];
};

type ReviewSubmissionFieldsWire = {
  status: string;
  latest_comment_author?: 'admin' | 'vendor' | null | undefined;
  resubmitted_at?: string | null | undefined;
  reviewed_at?: string | null | undefined;
  latest_admin_comment_at?: string | null | undefined;
};

/** The progress view of a wire question — field for field what web-shared's toProgressQuestion builds. */
export function progressQuestionFromWire(q: WireQuestion): ActionCountQuestion {
  return {
    id: q.id,
    type: q.type,
    required: q.required,
    requires_upload: q.requires_upload,
    parent_id: q.parent_id,
    condition: q.condition as { value: unknown } | null,
    visibility_rule: q.visibility_rule as ProgressQuestion['visibility_rule'],
    adoptable_doc_id: q.adoptable_doc_id,
    video_url: q.video_url ?? null,
    adoption_triggers: q.adoption_triggers.map((t) => ({
      trigger_value: t.trigger_value,
      document_id: t.document_id,
    })),
    questionnaire_id: q.questionnaire_id,
  };
}

/** The progress + review view of a wire answer — see progressQuestionFromWire. */
export function actionSubmissionFromWire(s: WireSubmission): ActionCountSubmission {
  const out: ActionCountSubmission = {
    question_id: s.question_id,
    answer: s.answer,
    status: s.status,
    submission_files: s.submission_files.map((f) => ({ id: f.id, option_key: f.option_key ?? null })),
    submission_adoptions: s.submission_adoptions.map((a) => ({
      document_id: a.document_id,
      option_key: a.option_key ?? null,
      ...(a.mode === 'adopted' || a.mode === 'uploaded' ? { mode: a.mode } : {}),
      signer_name: a.signer_name ?? null,
      signature_data_url: a.signature_data_url ?? null,
      requires_signature: a.requires_signature,
    })),
  };
  if (s.submitted_at !== undefined) out.submitted_at = s.submitted_at;
  if (s.latest_comment_author !== undefined) out.latest_comment_author = s.latest_comment_author;
  if (s.resubmitted_at !== undefined) out.resubmitted_at = s.resubmitted_at;
  if (s.reviewed_at !== undefined) out.reviewed_at = s.reviewed_at;
  if (s.latest_admin_comment_at !== undefined) {
    out.latest_admin_comment_at = s.latest_admin_comment_at;
  }
  return out;
}

/**
 * The nav-badge count from the wire shapes. The API (GET /api/me/nav-counts,
 * GET /api/driver/me) and the portal's fallback both call exactly this.
 */
export function vendorActionCountFromWire(
  questions: WireQuestion[],
  submissions: WireSubmission[],
): number {
  return vendorActionCount(
    questions.map(progressQuestionFromWire),
    submissions.map(actionSubmissionFromWire),
  );
}

export type ActionCountQuestion = ProgressQuestion & { questionnaire_id: string | null };
export type ActionCountSubmission = ProgressSubmission &
  ReviewSubmissionFields & { submitted_at?: string | null };

/**
 * The nav-badge total, summed per questionnaire (grouped by questionnaire_id,
 * each group through its own computeProgress — visibility rules never cross a
 * questionnaire; a question with no questionnaire_id belongs to none and is
 * skipped rather than pooled into an orphan bucket):
 *
 * - UNSUBMITTED questionnaire → exactly 1: "fill this in and submit it". This
 *   DELIBERATELY DEVIATES from the sister repo, which counts every unanswered
 *   required question from first login. Here the picker and the banner show no
 *   pre-submit count, the driver badge always meant "questionnaires to
 *   complete" (pending_questionnaire_count), and a per-question count jumped
 *   1 → 40 the moment the lists loaded.
 * - SUBMITTED questionnaire → its actionable items (returned answers only; see
 *   isActionable).
 */
export function vendorActionCount(
  questions: ActionCountQuestion[],
  submissions: ActionCountSubmission[],
): number {
  const subByQ = new Map(submissions.map((s) => [s.question_id, s]));
  const byQuestionnaire = new Map<string, ActionCountQuestion[]>();
  for (const q of questions) {
    if (q.questionnaire_id == null) continue;
    const list = byQuestionnaire.get(q.questionnaire_id);
    if (list) list.push(q);
    else byQuestionnaire.set(q.questionnaire_id, [q]);
  }
  let total = 0;
  for (const scoped of byQuestionnaire.values()) {
    if (!questionnaireSubmitted(scoped, subByQ)) {
      total += 1;
      continue;
    }
    total += questionnaireActionItems(scoped, subByQ, actionProgress(scoped, submissions), {
      submitted: true,
    }).length;
  }
  return total;
}
